import { FC, useState, useCallback } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AppPage, AppCard } from '../../components/general';
import { VoxProductCatalogItem, VoxRoiCostConfigData } from '../../models/voxRoi';
import styles from './VoxRoiConfigEditor.module.css';

export interface VoxRoiConfigEditorProps {
  config: VoxRoiCostConfigData;
  catalog: VoxProductCatalogItem[];
  onSave: (config: VoxRoiCostConfigData) => Promise<void>;
  onCancel: () => void;
}

const breadcrumbs = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'VoX ROI Savings', to: '/vox-roi' },
  { label: 'Edit Costs' },
];

const VoxRoiConfigEditor: FC<VoxRoiConfigEditorProps> = ({ config, catalog, onSave, onCancel }) => {
  const [productCosts, setProductCosts] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    for (const item of catalog) {
      initial[item.productKey] = config.productCosts[item.productKey] ?? 0;
    }
    return initial;
  });
  const [beforeSelection, setBeforeSelection] = useState<string[]>([...config.beforeSelection]);
  const [avgOrdersPerLoan, setAvgOrdersPerLoan] = useState(config.avgOrdersPerLoan);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCostChange = useCallback((productKey: string, value: number | null) => {
    setProductCosts((previous) => ({
      ...previous,
      [productKey]: value ?? 0,
    }));
  }, []);

  const handleBeforeToggle = useCallback((productKey: string) => {
    setBeforeSelection((previous) =>
      previous.includes(productKey)
        ? previous.filter((key) => key !== productKey)
        : [...previous, productKey],
    );
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        version: config.version,
        avgOrdersPerLoan,
        productCosts,
        beforeSelection,
      });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  }, [onSave, config.version, avgOrdersPerLoan, productCosts, beforeSelection]);

  const catalogRows = catalog.map((item) => ({
    ...item,
    cost: productCosts[item.productKey] ?? 0,
    isBefore: beforeSelection.includes(item.productKey),
  }));

  return (
    <AppPage title="Edit Cost Configuration" breadcrumbs={breadcrumbs}>
      <AppCard>
        <div className="flex flex-column gap-4">
          <div className="flex align-items-center gap-3" style={{ maxWidth: 320 }}>
            <label htmlFor="avg-orders" className="font-semibold text-sm white-space-nowrap">
              Avg orders / loan
            </label>
            <InputNumber
              inputId="avg-orders"
              value={avgOrdersPerLoan}
              onValueChange={(event) => setAvgOrdersPerLoan(event.value ?? 1)}
              minFractionDigits={1}
              maxFractionDigits={2}
              min={0.1}
              max={99}
              showButtons
              step={0.1}
            />
          </div>

          <DataTable value={catalogRows} size="small" stripedRows>
            <Column
              header="Before"
              body={(row) => (
                <Checkbox
                  checked={row.isBefore}
                  onChange={() => handleBeforeToggle(row.productKey)}
                  aria-label={`Before ${row.productDisplayName}`}
                />
              )}
              style={{ width: '5rem' }}
            />
            <Column field="vendor" header="Vendor" />
            <Column field="productDisplayName" header="Product" />
            <Column
              header="Cost ($)"
              style={{ width: '10rem', minWidth: '10rem' }}
              bodyClassName={styles.costCell}
              body={(row) => (
                <InputNumber
                  value={row.cost}
                  onValueChange={(event) => handleCostChange(row.productKey, event.value ?? 0)}
                  mode="currency"
                  currency="USD"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                />
              )}
            />
          </DataTable>

          {error && <Message severity="error" text={error} />}

          <div className="flex gap-2">
            <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={saving} />
            <Button label="Cancel" icon="pi pi-times" severity="secondary" outlined onClick={onCancel} disabled={saving} />
          </div>
        </div>
      </AppCard>
    </AppPage>
  );
};

export default VoxRoiConfigEditor;
